import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
// import { tracked } from '@glimmer/tracking';
// import { service } from '@ember/service';
// import { tracked } from '@glimmer/tracking';
// import { service } from '@ember/service';
import { next } from '@ember/runloop';

import $ from 'jquery';

import { TrackingEvent } from 'webapp/enums/tracking';
import { uniqueId } from 'webapp/utils/common';
import { isNumber } from 'webapp/utils/number';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockWrapperComponent from 'webapp/components/block/wrapper';
import type { BoardBlocksBaseArgs } from 'webapp/components/board/blocks/base';
import BoardBlocksBaseComponent from 'webapp/components/board/blocks/base';
import NoDataComponent from 'webapp/components/no-data';

import type Block from 'webapp/models-custom/block';
import NoRerenderSortable from 'webapp/models-custom/no-rerender-sortable';

type IndexMap = Record<number, number>;

export default class BoardBlocksMobileComponent extends BoardBlocksBaseComponent<BoardBlocksBaseArgs> {
	constructor(owner: Owner, args: BoardBlocksBaseArgs) {
		super(owner, args);

		this.noRerenderSortable = new NoRerenderSortable<Block>({
			elementSelector: '.preview-block',
			dataIdProperty: 'localId',
			dataSortProperty: 'order',
			getModel: () => {
				return this.blocks;
			},
			onNodeDelete: (el) => {
				el.remove();
			},
		});
	}

	initSortable(): void {
		if (this.args.isViewOnly) {
			return;
		}

		const blocksCt = this.el.find('.blocks');

		if (!blocksCt.length) {
			return;
		}

		const that = this;

		blocksCt.sortable({
			items: '> div:not(.temporary)',
			axis: 'y',
			scroll: true,
			scrollSensitivity: 150,
			scrollSpeed: 15,
			// handle: '.active-block span.sort',
			cursorAt: { bottom: 0 },
			placeholder: {
				element: function (currentItem: JQuery) {
					const el = $('<div />'),
						isNew = currentItem.hasClass('ui-draggable');
					if (isNew) {
						el.addClass('block drag-placeholder block-placeholder').html(
							'<div class="dashed-border">' +
								'<ui-icon name="DragAndDropArea" class="bounce"></ui-icon>' +
								'<div>Drop here</div>' +
								'</div>',
						);
					} else {
						el.addClass('sort-placeholder').html(
							'<div class="shadow"></div><div class="border"></div>',
						);
					}
					return el;
				},
				update: function () {
					return;
				},
			},
			helper: function (_event: JQuery.Event, el: JQuery) {
				return $('<div />')
					.addClass('sort-helper')
					.append(el.find('.active-block').clone())
					.css({
						height: '2.5em',
					})
					.appendTo(blocksCt);
			},
			update: function (_event: JQuery.Event, ui: jQueryUIInstance) {
				const isNew = ui.item.hasClass('temporary'),
					indexes = that.getIndexes(isNew);
				if (isNew) {
					const tempid = +ui.item.data('localid');
					// new block has been dragged in
					// we add it here because we need the right position
					const setupIdTmp = ui.item.data('setupid') as number | undefined; // a numeric data attribute
					const setupId = isNumber(setupIdTmp) ? +setupIdTmp : undefined; // set only for empty new visualizations

					void that.getBlockDataFromDropEvent(ui.item).then((blockData) => {
						blockData.localId = tempid;
						blockData.order = indexes[tempid];

						// added for tracking
						blockData.metricType = ui.item.data('metrictype') as string | undefined;

						blockData.fromMetricDateRange =
							that.args.boardState.commonMasterDateRange?.formula;

						that.addNewBlock(blockData, setupId, ui.item);
					});
					// ui.item.remove();
				} else {
					// ct.sortable('cancel');
				}
				next(that, that.updateSortOrder, indexes);

				if (!isNew) {
					// old blocks being sorted
					// do a request to update order
					that.existingBlockMoved(
						+ui.item.find('.block').attr('id')!.substr(1),
						indexes[+ui.item.data('localid')] || 0,
					);
				}
			},
			over: function () {
				$('.view-board-content .nodata.preview').hide();
				blocksCt.addClass('dragging');
			},
			out: function () {
				$('.view-board-content .nodata.preview').show();
				blocksCt.removeClass('dragging');
			},
			start: function (_event: JQuery.Event, ui: jQueryUIInstance) {
				$('.highcharts-tooltip-container').css({ opacity: 0 }); // hide chart tooltip when dragging

				const isSort = ui.helper.hasClass('sort-helper');

				if (isSort) {
					const height = ui.helper.height();

					// ensure that sortable item is same height as helper
					// so we can drop it anywhere (problem especially for multirow table)
					ui.item.css({
						height: height + 'px',
						visibility: 'hidden',
					});

					blocksCt.sortable('refreshPositions');

					ui.item.show();
					ui.helper.addClass('active-helper'); //for animation
					blocksCt.sortable('option', 'tolerance', 'pointer');
				}
			},
			beforeStop: function (_event: JQuery.Event, ui: jQueryUIInstance) {
				const isSort = ui.helper.hasClass('sort-helper');

				if (isSort) {
					ui.item.css({
						height: 'auto',
						visibility: 'visible',
					});

					blocksCt.find('.sort-helper').remove(); // remove helper
					blocksCt.sortable('option', 'tolerance', 'intersect');

					that.analytics.event(TrackingEvent.DESIGNER_BLOCK_REORDER);
				} else {
					// is new block dragged from block types
					ui.item
						.removeClass('ui-draggable ui-draggable-handle')
						.addClass('temporary')
						.data('localid', uniqueId())
						.html('');
				}
			},
			stop: function (_event: JQuery.Event, _ui: jQueryUIInstance) {
				$('.highcharts-tooltip-container').css({ opacity: 1 }); // show chart tooltip again
			},
		});
	}

	// ------ Position updating ------

	getIndexes(withTemporary = false): IndexMap {
		const indexes: IndexMap = {},
			el = this.el.find('.blocks'),
			selectors = ['.preview-block'];

		if (withTemporary) {
			selectors.push('li.temporary');
		}

		if (el.length) {
			el.find(selectors.join(',')).each(function (index: number) {
				const id = +$(this).data('localid');
				indexes[id] = index;
			});
		}

		return indexes;
	}

	updateSortOrder(indexes: IndexMap): void {
		this.blocks.forEach((item: Block) => {
			// set order directly on model since we won't be sending save
			item.directPropUpdate({
				order: indexes[item.localId],
			});
		});
	}

	existingBlockMoved(id: number, position: number): void {
		this.requestService
			.api('POST', '/blocks/' + id + '/order', { order: position })
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	// ------ START NO-RERENDER LOGIC -------

	@action
	checkBlockOrder(): void {
		this.noRerenderSortable.checkModelNodeOrder();
	}

	get blockOrder(): number[] {
		return this.blocks.map((block) => block.order);
	}

	// ------ END NO-RERENDER LOGIC	---------

	@action
	updateBlockDroppableArea(): void {
		const el = this.el;
		if (el) {
			// set min blocks height to .view-board-content height - so we can drop blocks wherever
			const totalHeight = el.closest('.view-board-content').height()!,
				headerHeight = el
					.closest('.view-board-content-inner')
					.find('.scroll-offset')
					.outerHeight()!,
				footerHeight = el
					.closest('.view-board-content-inner')
					.find('.bottomInfo')
					.outerHeight()!;
			const minHeight = String(totalHeight - headerHeight - footerHeight - 4) + 'px';
			el.find('.blocks').css({ minHeight });
		}
	}

	@action
	deleteBlock(block: Block): void {
		const prevBlock = this.blocks[this.blocks.indexOf(block) - 1];
		this.mmBlocksService.promptDeleteFromBoard(block, prevBlock);
	}

	@action
	moveUpOrDown(direction: string, block: Block): void {
		const { id, order } = block;

		if (id) {
			const blockEl = this.el.find(`.block#b${id}`),
				blockPreviewEl = blockEl.closest('.preview-block');

			let newOrder = -1;
			if (direction === 'up' && blockPreviewEl.prev().length) {
				blockPreviewEl.insertBefore(blockPreviewEl.prev());
				newOrder = order - 1;
			} else if (direction === 'down' && blockPreviewEl.next().length) {
				blockPreviewEl.insertAfter(blockPreviewEl.next());
				newOrder = order + 1;
			}

			if (newOrder > -1) {
				const indexes = this.getIndexes();

				this.updateSortOrder(indexes);
				this.existingBlockMoved(+id, newOrder);
			}
		}
	}

	@action
	override setupView(node: HTMLElement): void {
		super.setupView(node);
		this.initSortable();
		this.updateBlockDroppableArea();
	}

	@action
	teardownSortable(): void {
		const blocksEl = this.el.find('.blocks');
		if (blocksEl.length && blocksEl.sortable('instance')) {
			blocksEl.sortable('destroy');
		}
	}
	<template>
		<div
			{{didInsert this.setupView}}
			{{didUpdate (fn this.updateModel false) this.blocks}}
			{{didUpdate this.checkBlockOrder this.blockOrder}}
			{{willDestroy this.teardownSortable}}
			{{onWindowEvents this.updateBlockDroppableArea "resize" 300}}
		>
			{{#if this.showPlaceholder}}
				<NoDataComponent
					@type="preview"
					@title={{this.databoardContent.title}}
					@text={{this.databoardContent.text}}
					@icon={{this.databoardContent.icon}}
				/>
			{{/if}}
			<div class="blocks">
				{{#each this.noRerenderSortable.renderedModel as |block|}}
					<BlockWrapperComponent
						@model={{block}}
						@boardState={{@boardState}}
						@colorConfig={{this.colorConfig}}
						@isWallView={{false}}
						@currentZoom={{@currentZoom}}
						@skipBlockFetchLoader={{@skipBlockFetchLoaders}}
						@skipActions={{@skipActions}}
						@onBlockDelete={{fn this.deleteBlock block}}
						@onMoveUpOrDown={{this.moveUpOrDown}}
					/>
				{{/each}}
			</div>
		</div>
	</template>
}
